/* Client-side types for the job stream and API payloads. Features import from here (one place to switch).
   The API's own shapes come from `@cx/shared` (re-exported below as `Server*` and by name); the stream reader
   (`sse.ts` frameToEvent -> shared `toClientEvent`) adds the client field names used in `EventData` below
   (label, toolCallId, nested citation/artifact/approval with a numeric tier, approved/denied/expired,
   credits/inputTokens/outputTokens, run.finished `completed`) while keeping the API's fields, so both read.
   Every field the server might omit is optional. */
export type {
  OmniEvent as ServerEvent,
  EventDataMap as ServerEventDataMap,
  EventType as ServerEventType,
  Job as ServerJob,
  JobStatus as ServerJobStatus,
  User as ServerUser,
  AuthResponse,
  Entitlement,
  Balance,
  UsageMode,
  UsageTier,
  CreditBucket,
  ModelInfo,
  ModelTier,
  Effort,
  EffortLevel,
  UsageRow,
  UsageResponse,
  Health,
  ApiToken,
  ApprovalTier,
  ApprovalOutcome,
  ClientCitation,
  ClientArtifact,
  ClientApproval,
  ClientApprovalDecision,
  ApprovalDecisionBody,
  ChatJobInput,
  RouteEvent,
  RouteConfirm,
  RouteAlternative,
  RouteMode,
} from '@cx/shared';
import type { RouteConfirm as SharedRouteConfirm, RouteEvent as SharedRouteEvent } from '@cx/shared';

export const EVENT_TYPES = [
  'run.started', 'route', 'route.confirm', 'text.delta', 'text.done', 'reasoning.delta',
  'step.started', 'step.finished',
  'tool.call.started', 'tool.call.args', 'tool.call.result',
  'citation.added', 'artifact.created', 'artifact.updated',
  'approval.requested', 'approval.resolved',
  'usage.updated', 'state.snapshot', 'run.finished', 'run.error',
] as const;
export type EventType = (typeof EVENT_TYPES)[number];

/** SSE envelope: every event has `{id, seq, ts, type, data}`. */
export interface JobEvent<T extends EventType = EventType, D = unknown> {
  id: string;
  seq: number;
  ts: string | number;
  type: T;
  data: D;
}

export type StepStatus = 'queued' | 'running' | 'ok' | 'error' | 'skipped' | 'waiting' | 'warning';
export type RiskTier = 0 | 1 | 2 | 3;
export type ApprovalDecision = 'approved' | 'denied' | 'edited' | 'handoff' | 'expired';

export interface Citation {
  id: string;
  n?: number;
  url?: string;
  title: string;
  domain?: string;
  date?: string;
  type?: string;
  excerpt?: string;
  relevance?: number;
  cited?: boolean;
  lowQuality?: string;
}

export interface Artifact {
  id: string;
  kind: string;
  title: string;
  version: number;
  content?: string;
  mime?: string;
}

export interface Approval {
  id: string;
  tier: RiskTier;
  summary: string;
  preview?: string;
  agent?: string;
  step?: string;
  reversibility?: string;
  cost?: string;
  primaryLabel?: string;
  stepId?: string;
  /** False when "Allow for the rest of this task" is not offered (tiers 2-3 never offer it). */
  allowTask?: boolean;
  /** ISO time after which the server resolves it as timed out. */
  expiresAt?: string;
  /** Structured details from the server (e.g. money: merchant, items, subtotal, tax, total, shipTo). */
  details?: unknown;
  /** The API's tier word: read | change | consequential | sensitive. */
  tierName?: string;
}

export interface Usage {
  credits?: number;
  inputTokens?: number;
  outputTokens?: number;
  model?: string;
  provider?: string;
  byok?: boolean;
  /** Number of metered calls folded so far. */
  calls?: number;
}

/* Event payloads (data) by type. */
export interface EventData {
  'run.started': { jobId?: string; kind?: string; model?: string };
  /** Ask: what the server chose for the turn (mode, model, effort, thinking, estimate, alternatives). */
  route: SharedRouteEvent;
  /** Ask: the turn waits for POST .../messages/:mid/route/confirm before it spends. */
  'route.confirm': SharedRouteConfirm;
  'text.delta': { delta: string; messageId?: string };
  'text.done': { text?: string; messageId?: string };
  'reasoning.delta': { delta: string };
  'step.started': { stepId: string; label: string; title?: string; kind?: string; target?: string };
  'step.finished': { stepId: string; status?: StepStatus; durationMs?: number; summary?: string };
  'tool.call.started': { toolCallId: string; callId?: string; name: string; stepId?: string; label?: string };
  'tool.call.args': { toolCallId: string; callId?: string; delta?: string; args?: unknown };
  'tool.call.result': { toolCallId: string; callId?: string; result?: unknown; isError?: boolean; error?: string; durationMs?: number };
  'citation.added': { citation: Citation };
  'artifact.created': { artifact: Artifact };
  'artifact.updated': { artifactId: string; version?: number; content?: string; title?: string };
  'approval.requested': {
    approval: Approval;
    /* The API's flat fields (kept next to the nested client copy). */
    approvalId?: string; tier?: string; summary?: string; details?: unknown; expiresAt?: string; allowTask?: boolean;
  };
  'approval.resolved': { approvalId: string; decision: ApprovalDecision; by?: string; outcome?: string; auto?: boolean; editedPreview?: string };
  'usage.updated': Usage & { costCredits?: number; tokensIn?: number; tokensOut?: number; kind?: string; units?: number };
  'state.snapshot': { status?: string; state?: unknown };
  'run.finished': { status?: 'completed' | 'cancelled'; model?: string; durationMs?: number; output?: unknown };
  'run.error': { code: string; message: string; retryable?: boolean };
}

export type AnyJobEvent = { [K in EventType]: JobEvent<K, EventData[K]> }[EventType];

/** The subset of folded state the server may send in `state.snapshot`. */
export interface SnapshotState {
  text: string;
  reasoning: string;
  status: JobStatus;
}

export type JobStatus = 'idle' | 'running' | 'waiting' | 'completed' | 'cancelled' | 'error';

/* HTTP payloads */
export interface ApiErrorBody { error: { code: string; message: string } }

export interface CreateJobRequest<I = unknown> { kind: string; input: I }
export interface CreateJobResponse { id: string }

export interface User {
  id: string;
  email: string;
  name?: string;
  initials?: string;
  plan?: string;
  credits?: { used: number; allowance: number; resetsAt?: string; addon?: number; mode?: 'byok' | 'payg' | 'limits' | 'addon' };
}
export interface SessionResponse { user: User | null }

export interface ChatInput {
  messages: { role: 'user' | 'assistant'; content: string }[];
  model?: string;
  effort?: string;
  mode?: string | null;
  threadId?: string;
}
